/**
 * Data-access permission map.
 *
 * Mirrors the backend contract in `api/Util/PermissionsUtil.py`
 * (`get_permission_to_view_data` / `_delete_data` / `_override_data`). The guarded
 * endpoints answer 403, so the frontend must suppress requests for a denied stream
 * rather than let them fire and fail.
 *
 * `override`/`remove` are null where the backend defines no such permission for the
 * stream: wellness and rpe cannot be overridden, and rpe has no view gate at all.
 */

export type DataStream =
  | "gps"
  | "wellness"
  | "forceplate"
  | "testing"
  | "nutrition"
  | "cognition"
  | "helmet"
  | "rpe";

export type DataStreamPermissions = {
  readonly label: string;
  readonly access: string | null;
  readonly override: string | null;
  readonly remove: string | null;
};

export const DATA_STREAM_PERMISSIONS: Record<DataStream, DataStreamPermissions> = {
  gps: {
    label: "GPS",
    access: "AccessGpsData",
    override: "OverrideGpsDataPoints",
    remove: "DeleteGpsDataPoints",
  },
  wellness: {
    label: "Wellness",
    access: "AccessWellnessData",
    override: null,
    remove: "DeleteWellnessDataPoints",
  },
  forceplate: {
    label: "Force Plate",
    access: "AccessForceplateData",
    override: "OverrideForceplateDataPoints",
    remove: "DeleteForceplateDataPoints",
  },
  testing: {
    label: "Testing",
    access: "AccessTestingData",
    override: "OverrideTestingDataPoints",
    remove: "DeleteTestingDataPoints",
  },
  nutrition: {
    label: "Nutrition",
    access: "AccessNutritionData",
    override: "OverrideNutritionDataPoints",
    remove: "DeleteNutritionDataPoints",
  },
  cognition: {
    label: "Cognition",
    access: "AccessCognitionData",
    override: "OverrideCognitionDataPoints",
    remove: "DeleteCognitionDataPoints",
  },
  helmet: {
    label: "Helmet Impact",
    access: "AccessHelmetData",
    override: "OverrideHelmetDataPoints",
    remove: "DeleteHelmetDataPoints",
  },
  rpe: {
    label: "RPE",
    access: null,
    override: null,
    remove: "DeleteRpeDataPoints",
  },
};

export const DATA_STREAMS = Object.keys(DATA_STREAM_PERMISSIONS) as DataStream[];

/** Streams that can be viewed, i.e. that have an `Access*Data` permission. */
export const VIEWABLE_DATA_STREAMS = DATA_STREAMS.filter(
  (stream) => DATA_STREAM_PERMISSIONS[stream].access !== null
);

export const dataStreamLabel = (stream: DataStream): string =>
  DATA_STREAM_PERMISSIONS[stream]?.label ?? stream;

/**
 * Resolves one permission key against a user's grants.
 *
 * A null key means the backend defines no permission for that action, which is an
 * unrestricted action rather than a denied one. The team's `platform_permissions_enabled`
 * flag plays no part: the backend enforces these keys on every team.
 */
export const hasDataPermission = (
  permissionKey: string | null,
  userPermissions: Record<string, boolean> | null | undefined
): boolean => {
  if (permissionKey === null) return true;
  return Boolean(userPermissions?.[permissionKey]);
};
